import { convertUTCToLocalWithOffset } from "@/lib/utils";
import { CalendarBlockType } from "@/types/enums";
import { ReservationTypeMapping } from "@/api/reservationTypeMappings/types";

// Default fallback values
export const blockTypeMapFallback = "Other Block";
export const blockColorMapFallback = "bg-chicago-100 text-chicago-950";

/**
 * Gets the display mapping for a block type using the database mappings.
 * Falls back to default values if type is not found.
 */
export const getBlockTypeMapping = (
  type: string,
  mappingsMap: Record<string, ReservationTypeMapping>
): { displayLabel: string; colorClass: string; displayCategory: string; isHidden: boolean } => {
  const mapping = mappingsMap[type];
  if (mapping) {
    return {
      displayLabel: mapping.displayLabel,
      colorClass: mapping.colorClass,
      displayCategory: mapping.displayCategory,
      isHidden: mapping.displayCategory === "HIDDEN",
    };
  }
  // Default fallback for unknown types
  return {
    displayLabel: blockTypeMapFallback,
    colorClass: blockColorMapFallback,
    displayCategory: "OTHER_BLOCK",
    isHidden: false,
  };
};

/**
 * Normalizes calendar block types to their canonical display category.
 * Uses database mappings when available, falls back to legacy logic.
 */
export const normalizeBlockType = (
  type: string,
  mappingsMap?: Record<string, ReservationTypeMapping>
): CalendarBlockType => {
  // If mappings are provided, use them
  if (mappingsMap && Object.keys(mappingsMap).length > 0) {
    const mapping = mappingsMap[type];
    if (mapping) {
      switch (mapping.displayCategory) {
        case "OWNER_BLOCK":
          return CalendarBlockType.OWNER_BLOCK;
        case "BOOKING":
          return CalendarBlockType.BOOKING;
        case "HIDDEN":
          return CalendarBlockType.PREVIOUS_BOOKING;
        case "OTHER_BLOCK":
        default:
          return CalendarBlockType.OTHER;
      }
    }
    return CalendarBlockType.OTHER;
  }

  // Legacy fallback logic (when mappings not yet loaded)
  if (type === "OWNER_BLOCK" || type === "Owner Block") {
    return CalendarBlockType.OWNER_BLOCK;
  }
  if (type === "BOOKING") {
    return CalendarBlockType.BOOKING;
  }
  if (type === "PREVIOUS_BOOKING") {
    return CalendarBlockType.PREVIOUS_BOOKING;
  }
  return CalendarBlockType.OTHER;
};

/**
 * Reformats calendar block data with display information.
 * Uses database mappings for type/color lookup.
 */
export const blockNoticesReformat = (
  data: CalendarBlockBE[],
  mappingsMap?: Record<string, ReservationTypeMapping>
) =>
  data
    .map((block: CalendarBlockBE) => {
      const typeMapping = getBlockTypeMapping(block.type as string, mappingsMap ?? {});
      const normalizedType = normalizeBlockType(block.type as string, mappingsMap);
      return {
        block: {
          ...block,
          type: normalizedType,
          start: convertUTCToLocalWithOffset(block.start),
          end: convertUTCToLocalWithOffset(block.end),
        },
        label: (typeMapping.displayLabel || blockTypeMapFallback) + (block.unitName ? ` — ${block.unitName}` : ""),
        color: typeMapping.colorClass || blockColorMapFallback,
        isHidden: typeMapping.isHidden,
      };
    })
    .filter((item) => !item.isHidden); // Filter out hidden types
